import { AlertTriangle, TrendingUp, Target, Activity } from "lucide-react";

export default function RiskSection() {
  const risks = [
    {
      icon: TrendingUp,
      text: "Pressão estrutural aumentando silenciosamente",
    },
    {
      icon: Activity,
      text: "Backlog acelerando gradualmente",
    },
    {
      icon: Target,
      text: "Metas viáveis no papel, mas insustentáveis na prática",
    },
    {
      icon: AlertTriangle,
      text: "Estrutura absorvendo esforço além do saudável",
    },
  ];

  return (
    <section
      id="risk"
      className="relative py-16 md:py-32 bg-[#0A0B0E]"
      data-testid="risk-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Label */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 text-xs font-mono tracking-widest uppercase text-[#F59E0B] bg-[#0E3A46] border border-[#F59E0B]/20 rounded-sm mb-6">
            O Risco Invisível
          </span>
        </div>

        {/* Main Title */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#E5E7EB] mb-4">
            O maior risco não é errar.
          </h2>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F59E0B] mb-8">
            É não perceber que está errando.
          </h2>
          <p className="text-base md:text-lg text-[#9CA3AF]">
            Toda operação em crescimento emite sinais.
          </p>
          <p className="text-base md:text-lg text-[#9CA3AF] mt-6 max-w-2xl mx-auto leading-relaxed">
            Muitas decisões operacionais parecem corretas no presente, mas revelam seus efeitos apenas semanas depois, quando a dinâmica da operação já mudou.
          </p>
        </div>

        {/* Risk Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
          {risks.map((risk, index) => (
            <div
              key={index}
              className="flex items-center gap-4 p-6 bg-[#1C1F26] border border-[#2A2F38] rounded-sm transition-all hover:border-[#F59E0B]/50"
              data-testid={`risk-card-${index}`}
            >
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#0E3A46] rounded-sm">
                <risk.icon className="h-6 w-6 text-[#F59E0B]" />
              </div>
              <p className="text-[#E5E7EB] font-medium">{risk.text}</p>
            </div>
          ))}
        </div>

        {/* Structural Limit */}
        <div className="max-w-2xl mx-auto mb-16 px-6 md:px-0">
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6 md:p-8 text-center">
            <p className="text-[#E5E7EB] text-base md:text-lg font-medium mb-2">
              Toda operação possui um limite estrutural.
            </p>
            <p className="text-[#9CA3AF] text-sm md:text-base">
              O desafio não é alcançá-lo — é perceber quando ele está se aproximando.
            </p>
          </div>
        </div>

        {/* Warning Text */}
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[#9CA3AF] text-base md:text-lg mb-4">
            Esses sinais não aparecem no extrato bancário.
            <br />
            <span className="text-[#E5E7EB] font-medium">Eles aparecem na estrutura.</span>
          </p>
          <div className="w-16 h-px bg-[#F59E0B] mx-auto my-8" />
          <p className="text-[#E5E7EB] text-lg md:text-xl font-medium">
            Empresas que quebram raramente quebram de repente.
          </p>
          <p className="text-[#F59E0B] text-lg md:text-xl font-medium mt-2">
            Elas perdem previsibilidade antes.
          </p>
        </div>

        {/* Identification Block */}
        <div className="max-w-3xl mx-auto mt-16">
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-8 md:p-10 text-center">
            <p className="text-base md:text-lg text-[#E5E7EB] font-medium mb-6">
              Se a sua operação hoje se comporta assim:
            </p>
            <div className="text-left max-w-xl mx-auto space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">Você está produzindo no limite quase todos os dias</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">Pequenos atrasos começam a aparecer com mais frequência</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">O backlog cresce mesmo com esforço alto da equipe</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">A sensação é de que a operação está sempre correndo atrás</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">E mesmo assim, ainda parece que tudo está sob controle</span>
              </div>
            </div>
            <div className="border-t border-[#2A2F38] pt-6 mt-4">
              <p className="text-[#F59E0B] text-sm md:text-base font-medium">
                Então sua operação já está mudando de comportamento — mesmo que ainda não seja evidente no dia a dia.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
